import { useEffect, useState, useRef, useMemo } from "react";
import { fetchData } from "../hooks/fetchData";
import { IconUserImages, IconUserChristmasImages } from "../components/elements/Icon";
import { checkChristmas } from "../hooks/useCheckChristmas";
import { useSelector } from 'react-redux';
import { isDev, isProd } from "../config";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";

const CATEGORIES = [
  { label: "counting", value: "counting" },
  { label: "shapes", value: "shapes" },
  { label: "adding", value: "adding" },
  { label: "subtracting", value: "subtract" },
  { label: "multiplication", value: "multipl" },
  { label: "division", value: "division" },
  { label: "sorting", value: "sorting" },
  { label: "compare", value: "compare" },
  { label: "memory", value: "memory" },
  { label: "schulte", value: "schulte" },
  { label: "fraction", value: "fraction" }
];

const PERIODS = [
  { label: "Daily", value: "daily" },
  { label: "All Time", value: "all" }
];

export const TopScoresTable = ({ tableRef }) => {
  const meRowRef = useRef(null);
  const [isMeVisible, setIsMeVisible] = useState(true);

  const tableContainerRef = useRef(null);
  const categoriesRef = useRef(null);
  const lang = localStorage.getItem('i18nextLng') || 'en'
  const regionNames = new Intl.DisplayNames([lang], { type: 'region' });

  const [scores, setScores] = useState([]);
  const [openCategories, setOpenCategories] = useState(false);
  const [period, setPeriod] = useState(() => localStorage.getItem("period") || "daily");
  const [scope, setScope] = useState(() => localStorage.getItem("scope") || "world");

  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);
  const user = useSelector((state) => state.user)
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const [hasMore, setHasMore] = useState(true);
  const { t } = useTranslation();
  const [meUser, setMeUser] = useState(null);

  const [showCategoryButton, setShowCategoryButton] = useState(() => {
    const scope = localStorage.getItem("scope");
    return scope !== "world" && scope !== "country" && scope !== null;
  });
  const basePath = isDev
    ? "https://sandbox.mathmindsgame.com:3080"
    : isProd
      ? "https://mathmindsgame.com:3080"
      : "http://127.0.0.1:3000";



  const deviceToken = user.user?.userEmail
  const _id = user.selectedProfile?._id
  useEffect(() => {
    const category = CATEGORIES.find(c => c.value === scope);
    if (category) setSelectedCategory(category);
  }, [scope]);
  useEffect(() => {
    localStorage.setItem("period", period);
  }, [period]);

  useEffect(() => {
    localStorage.setItem("scope", scope);
  }, [scope]);

  useEffect(() => {
    const fetchTopScores = async () => {
      try {
        setLoading(true);

        const response = await fetchData(
          `/score/top?scope=${scope}&period=${period}&skip=${skip}&limit=${limit}`,
          "get",
          null,
          {
            devicetoken: deviceToken,
            id: _id
          }
        );

        const top = response?.data?.data?.top || [];
        const me = response?.data?.data?.me || null;

        setScores(prev => (
          skip === 0 ? top : [...prev, ...top]
        ));

        if (me && skip === 0) {
          setMeUser(me);
        }

        if (top.length < limit) {
          setHasMore(false);
        }

      } catch (err) {
        console.error("Failed to fetch top scores", err);
        setScores([]);
        setMeUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTopScores();
  }, [scope, period, skip, deviceToken, _id]);

  const tableData = useMemo(() => {
    if (!meUser) {
      return scores.map(item => ({
        ...item,
        isMe: false
      }));
    }

    const meInTop = scores.some(
      item => item.nickname === meUser.nickname && item.avatar === meUser.avatar
    );

    if (meInTop) {
      return scores.map(item => ({
        ...item,
        isMe: item.nickname === meUser.nickname && item.avatar === meUser.avatar
      }));
    }

    return [
      ...scores.map(item => ({
        ...item,
        isMe: false
      })),
      {
        ...meUser,
        isMe: true,
        isOutOfTop: true
      }
    ];
  }, [scores, meUser]);


  useEffect(() => {
    setScores([]);
    setSkip(0);
    setHasMore(true);
  }, [scope, period]);



  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const preloadOffset = 200; 

      if (
        scrollTop + clientHeight >= scrollHeight - preloadOffset &&
        !loading &&
        hasMore
      ) {
        setSkip((prev) => prev + limit);
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [loading, hasMore]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openCategories &&
        categoriesRef.current &&
        !categoriesRef.current.contains(e.target)
      ) {
        setOpenCategories(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openCategories]);

  useEffect(() => {
    if (tableData.length === 1) {
      setIsMeVisible(true);
      return; 
    }

    const meEl = meRowRef.current;
    const container = tableContainerRef.current;
    if (!meEl || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsMeVisible(entry.isIntersecting);
      },
      {
        root: container,
        threshold: 0.1,
      }
    );

    observer.observe(meEl);

    return () => observer.disconnect();
  }, [tableData, scope, period]);

  return (
    <div
      className="w-full relative xl:mt-[44px] lg:mt-[28px] md:mt-[24px] sm:mt-[16px] mt-[16px] font-commissioner font-bold"
      style={{
        width: "80%",
        height: "100%"
      }}>
      <div className="lg:text-[20px] md:text-[16px] sm:text-[16px] text-[14px]">
        {/* TOP CONTROLS */}
        <div className="flex justify-center items-center xl:gap-[18px] lg:gap-[16px] md:gap-[12px] sm:gap-[11.26px] gap-[11.26px] xl:mb-[24px] lg:mb-[20px] md:mb-[16px] lg:mb-[16px] mb-[16px]">

          {/* World */}
          <button
            onClick={() => {
              setShowCategoryButton(false);
              setScope("world");
              setSelectedCategory(null);
            }}
            className={`xl:w-[193px] lg:w-[169px] md:w-[131px] sm:w-[123.15px] w-[123.15px]
              xl:h-[48px] lg:h-[42px] md:h-[32.57px] sm:h-[30.6px] h-[30.6px] transition rounded-[8px]`}
            style={{
              backgroundImage: "url('/table/button.svg')",
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              backgroundPosition: "center",
              filter: scope === "world" ? "brightness(1.2)" : "none",
              color: scope === "world" ? "#FFFFFF" : "#E3DDE9",
              backgroundColor: scope === "world" ? "#9C64E0" : "#7F48C2",
            }}
          >
            {t("world")}
          </button>

          {/* Country */}
          {user.user?.country && (
            <button
              onClick={() => {
                setShowCategoryButton(false);
                setScope("country");
                setSelectedCategory(null);
              }}
              className={`xl:w-[193px] lg:w-[169px] md:w-[131px] sm:w-[123.15px] w-[123.15px]
              xl:h-[48px] lg:h-[42px] md:h-[32.57px] sm:h-[30.6px] h-[30.6px]  rounded-[8px]`}
              style={{
                backgroundImage: "url('/table/button.svg')",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
                filter: scope === "country" ? "brightness(1.2)" : "none",
                color: scope === "country" ? "#FFFFFF" : "#E3DDE9",
                backgroundColor: scope === "country" ? "#9C64E0" : "#7F48C2",
              }}
            >
              {user.user?.country
                ? regionNames.of(user.user.country)
                : "Country"}

            </button>
          )}

          {/* Categories */}
          <div className="relative">
            <button
              onClick={() => {
                setScope("counting")
                setShowCategoryButton(true);
                setOpenCategories(false);
              }}

              className={`xl:w-[193px] lg:w-[169px] md:w-[131px] sm:w-[123.15px] w-[123.15px]
              xl:h-[48px] lg:h-[42px] md:h-[32.57px] sm:h-[30.6px] h-[30.6px] transition rounded-[8px]`}
              style={{
                backgroundImage: "url('/table/button.svg')",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
                filter: ((scope !== "world" && scope !== "country") && scope) ? "brightness(1.2)" : "none",
                color: ((scope !== "world" && scope !== "country") && scope) ? "#FFFFFF" : "#E3DDE9",
                backgroundColor: ((scope !== "world" && scope !== "country") && scope) ? "#9C64E0" : "#7F48C2",
              }}
            >
              {t("category")}
            </button>
          </div>
        </div>


        <div className="flex justify-center items-center xl:gap-[18px] lg:gap-[16px] md:gap-[12px] sm:gap-[11.26px] gap-[11.26px] xl:mb-[24px] lg:mb-[20px] md:mb-[16px] lg:mb-[16px] mb-[16px]">
          {showCategoryButton && (
            <div ref={categoriesRef}
              className="relative xl:w-[267px] lg:w-[241px] md:w-[201px] sm:w-[178px] w-[178px]
                       xl:h-[48px] lg:h-[42px] md:h-[32px] sm:h-[28px] h-[28px]
                       rounded-[8px]"
              style={{
                backgroundImage: "url('/table/categories.svg')",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenCategories((prev) => !prev);
                }}
                className="relative w-full h-full text-white rounded-[8px] bg-transparent px-4"
              >
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 truncate pointer-events-none">
                  {t(selectedCategory ? selectedCategory.label : "counting")}
                </span>

                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  ▼
                </span>
              </button>






              {openCategories && (
                <div

                  className="absolute xl:top-11 lg:top-9 md:top-7 top-6 mt-2 right--5 bg-[#F0ECF5] rounded-[8px]
               border-[3px] border-solid border-[#8149C4] overflow-hidden z-50 xl:w-[267px] lg:w-[241px] md:w-[201px] sm:w-[178px] w-[178px] xl:h-[279px] lg:h-[250px] md:h-[210px] sm:h-[189px] h-[189px]"
                  style={{
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#8149C4 #F0ECF5",
                  }}
                >
                  {CATEGORIES.map((cat, index) => (
                    <div key={cat.value} >
                      <button
                        onClick={() => {
                          setSelectedCategory(cat);
                          setScope(cat.value);
                          setOpenCategories(false);
                        }}
                        className={`block w-full text-left px-2 xl:text-[16px] lg:text-[14px] md:text-[12px] sm:text-[10px] text-[10px] font-fredokaOne font-normal 
                        xl:p-[4px] lg:p-[3px] md:p-[2px] sm:p-[1px] p-[1px]
                      ${selectedCategory?.value === cat.value
                            ? 'text-[#8149C4]'
                            : 'text-[#8B7B9E]'} 
                      hover:bg-purple-100`}
                      >
                        {t(cat.label)}
                      </button>
                      {index < CATEGORIES.length - 1 && (
                        <hr className="border-t-[1px] border-[#8149C4] my-1" />
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          )}


          <div
            onClick={() =>
              setPeriod((prev) => (prev === "daily" ? "all" : "daily"))
            }
            className="flex items-center xl:h-[48px] lg:h-[42px] md:h-[32px] sm:h-[28px] h-[28px] bg-[#B2CA23] rounded-[8px] p-[3px] cursor-pointer select-none"
          >
            <div
              className={`px-4  rounded-[8px] transition-all
      ${period === "all"
                  ? "bg-[#FED241] text-[#fff]"
                  : "text-[#fff]"
                }`}
            >
              {t("all")}
            </div>

            <div
              className={`px-4  rounded-[8px] transition-all
      ${period === "daily"
                  ? "bg-[#FED241] text-[#fff]"
                  : "text-[#fff]"
                }`}
            >
              {t("today")}
            </div>
          </div>
        </div>
      </div>


      {/* TABLE */}
      <div
        ref={tableContainerRef}
        className="bg-white xl:max-h-[390px] lg:max-h-[340px] md:max-h-[295px] sm:max-h-[275px] max-h-[275px]"
        style={{
          border: "3px solid #BED72D",
          borderRadius: "14px",
          overflow: "hidden",
          overflowY: "auto",
        }}
      >
        <table
          className="w-full table-fixed  xl:text-[20px] lg:text-[16px] md:text-[14px] sm:text-[12px] text-[12px] text-[#4B5563] bg-white border-collapse"
        >
          <thead className="text-[#7F48C2] ">
            <tr
              className="border-b"
              style={{ borderColor: "rgba(190, 215, 45, 0.4)" }}
            >
              <th className="py-2  text-left xl:pl-[43px] lg:pl-[43px] md:pl-[30px] sm:pl-[29px] pl-[29px]">
                {t("profileName")}
              </th>

              {scope !== "country" && <th className="py-2 w-[150px] text-center">
                {t("country")}
              </th>}

              <th className="py-2 text-right w-[80px] xl:pr-[20px] lg:pr-[18px] md:pr-[16px] sm:pr-[14px] pr-[14px]">
                {t("score")}
              </th>
            </tr>
          </thead>

          <tbody className="text-[#4B96C9]">
            {(tableData.length > 0) ? (
              <>
                {tableData.map((item, index) => {
                  const place = item.isOutOfTop ? item.position : index + 1

                  return (
                    <tr
                      ref={item.isMe ? meRowRef : null}
                      key={`${item.nickname}-${item.totalPoints}-${index}`}
                      className="border-b"
                      style={{
                        borderColor: "rgba(190, 215, 45, 0.4)", backgroundColor: item.isMe
                          ? "rgba(240, 236, 245, 1)"
                          : "transparent",
                      }}
                    >
                      <td className="flex items-center min-w-0">
                        {/* Place */}
                        <div className="flex items-center justify-center flex-shrink-0 xl:w-[68px] lg:w-[56px] md:w-[52px] sm:w-[44px] w-[44px] xl:h-[81px] lg:h-[68px] md:h-[52px] sm:h-[54px] h-[54px] relative text-white">
                          {place <= 3 ? (
                            <div className="relative w-full h-full flex items-center justify-center">
                              <img
                                src="/table/top3.svg"
                                alt="Top 3"
                                className="xl:w-[32px] lg:w-[24px] md:w-[22px] sm:w-[18px] w-[18px] xl:h-[36px] lg:h-[27.13px] md:h-[24.82px] sm:h-[20px] h-[20px]"
                              />
                              <span className="absolute inset-0 flex items-center xl:-top-2 -top-1 justify-center ">
                                {place}
                              </span>
                            </div>
                          ) : (
                            <div className="relative w-full h-full flex items-center justify-center">
                              <img
                                src="/table/after_3.svg"
                                alt="Top"
                                className="xl:w-[29px] lg:w-[23px] md:w-[20px] sm:w-[18px] w-[18px] xl:h-[29px] lg:h-[23px] md:h-[20px] sm:h-[18px] h-[18px]"
                              />
                              <span className="absolute inset-0 flex items-center justify-center">
                                {place}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Avatar */}
                        <div className="flex-shrink-0 xl:w-[57px] lg:w-[48px] md:w-[40px] sm:w-[38px] w-[38px] xl:h-[57px] lg:h-[48px] md:h-[40px] sm:h-[38px] h-[38px] rounded-full overflow-hidden bg-[#ACC587] border border-[#7743BA] flex items-center justify-center">
                          {checkChristmas() ? (
                            <IconUserChristmasImages
                              user={item.avatar}
                            //className={item.avatar === 'avatar 7_all' ? "-translate-x-[15%]" : ""}
                            />
                          ) : (
                            <IconUserImages
                              user={item.avatar}
                            //className={item.avatar === 'avatar 7_all' ? "-translate-x-[15%]" : ""}
                            />
                          )}
                        </div>

                        {/* Nickname */}
                        <span className="flex-1 min-w-0 truncate xl:ml-[16px] lg:ml-[16px] md:ml-[14px] sm:ml-[12px] ml-[12px]" title={item.nickname}>
                          {item.nickname}
                        </span>
                      </td>


                      {scope !== "country" && <td className="py-2 text-center truncate">
                        {item.cc ? (
                          <div className="flex flex-col items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap">
                            <div
                              className="
                                       bg-contain bg-no-repeat bg-center
                                       xl:w-[28px] lg:w-[24px] md:w-[20px] sm:w-[18px] w-[18px]
                                       xl:h-[19px] lg:h-[16px] md:h-[13px] sm:h-[12px] h-[12px]
                                       border
                                       bg-[image:var(--flag)] bg-cover bg-center bg-no-repeat
                                     "
                              style={{ "--flag": `url(${basePath}/images/flags/${item.cc}.svg)` }}
                            />
                            <span className="xl:text-[14px] lg:text-[12px] md:text-[10px] sm:text-[10px] text-[10px] truncate">
                              {regionNames.of(item.cc)}
                            </span>
                          </div>
                        ) : null}
                      </td>}


                      <td className="xl:pr-[25px] lg:pr-[23px] md:pr-[22px] sm:pr-[16px] pr-[16px] text-right truncate">
                        {item.totalPoints}
                      </td>
                    </tr>
                  )
                })
                }
                {!isMeVisible && meUser && (
                  <tr
                    className="bg-[#F0ECF5] border-t border-[#BED72D] sticky bottom-0 z-10"
                  >
                    <td className="flex items-center min-w-0">
                      {/* Place */}
                      <div className="flex items-center justify-center flex-shrink-0 xl:w-[68px] lg:w-[56px] md:w-[52px] sm:w-[44px] w-[44px] xl:h-[81px] lg:h-[68px] md:h-[52px] sm:h-[54px] h-[54px] relative text-white">
                        {meUser.position <= 3 ? (
                          <div className="relative w-full h-full flex items-center justify-center">
                            <img
                              src="/table/top3.svg"
                              alt="Top 3"
                              className="xl:w-[32px] lg:w-[24px] md:w-[22px] sm:w-[18px] w-[18px] xl:h-[36px] lg:h-[27.13px] md:h-[24.82px] sm:h-[20px] h-[20px]"
                            />
                            <span className="absolute inset-0 flex items-center xl:-top-2 -top-1 justify-center ">
                              {meUser.position}
                            </span>
                          </div>
                        ) : (
                          <div className="relative w-full h-full flex items-center justify-center">
                            <img
                              src="/table/after_3.svg"
                              alt="Top"
                              className="xl:w-[29px] lg:w-[23px] md:w-[20px] sm:w-[18px] w-[18px] xl:h-[29px] lg:h-[23px] md:h-[20px] sm:h-[18px] h-[18px]"
                            />
                            <span className="absolute inset-0 flex items-center justify-center">
                              {meUser.position}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Avatar */}
                      <div className="flex-shrink-0 xl:w-[57px] lg:w-[48px] md:w-[40px] sm:w-[38px] w-[38px] xl:h-[57px] lg:h-[48px] md:h-[40px] sm:h-[38px] h-[38px] rounded-full overflow-hidden bg-[#ACC587] border border-[#7743BA] flex items-center justify-center">
                        {checkChristmas() ? (
                          <IconUserChristmasImages
                            user={meUser.avatar}
                          />
                        ) : (
                          <IconUserImages
                            user={meUser.avatar}
                          />
                        )}
                      </div>
                      {/* Nickname */}
                      <span className="flex-1 min-w-0 truncate xl:ml-[16px] lg:ml-[16px] md:ml-[14px] sm:ml-[12px] ml-[12px]" title={meUser.nickname}>
                        {meUser.nickname}
                      </span>
                    </td>

                    {scope !== "country" && <td className="py-2 text-center truncate">
                      {meUser.cc ? (
                        <div className="flex flex-col items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap">
                          <div
                            className="
                              bg-contain bg-no-repeat bg-center
                              xl:w-[28px] lg:w-[24px] md:w-[20px] sm:w-[18px] w-[18px]
                              xl:h-[19px] lg:h-[16px] md:h-[13px] sm:h-[12px] h-[12px]
                              border
                              bg-[image:var(--flag)] bg-cover bg-center bg-no-repeat
                            "
                            style={{ "--flag": `url(${basePath}/images/flags/${meUser.cc}.svg)` }}
                          />
                          <span className="xl:text-[14px] lg:text-[12px] md:text-[10px] sm:text-[10px] text-[10px] truncate">
                            {regionNames.of(meUser.cc)}
                          </span>
                        </div>
                      ) : null}
                    </td>}

                    <td className="xl:pr-[25px] lg:pr-[23px] md:pr-[22px] sm:pr-[16px] pr-[16px] text-right truncate">
                      {meUser.totalPoints}
                    </td>
                  </tr>
                )}

              </>
            ) : (
              !loading && (<tr>
                <td colSpan="3" className="text-center py-4">
                  {t("noData")}
                </td>
              </tr>)
            )}
          </tbody>
        </table>

        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <img
              src="/loading/loading.svg"
              alt="Loading"
              className="xl:w-[32px] lg:w-[28px] md:w-[24px] sm:w-[20px] w-[20px] animate-spin"
            />
          </div>
        )}
      </div>
    </div>
  );

}

export default TopScoresTable;